// src/planner/plannerStyles.js
import styled, { css, keyframes } from 'styled-components';
import { color, font } from '../../theme/tokens';

// --- Animations ---
export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Layout ---
// The planner board sits in the PageShell content column below the horizon strip.
export const PageContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    height: clamp(560px, calc(100vh - 120px), 960px);
    background: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    font-family: ${font.body};
    overflow: hidden;

    /* corner ticks */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        pointer-events: none;
        z-index: 50;
    }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }

    @media (max-width: 860px) {
        height: auto;
    }
`;

export const PlannerLayout = styled.div`
    display: flex;
    flex-grow: 1;
    min-height: 0;
    position: relative;
    z-index: 1;

    @media (max-width: 860px) {
        flex-direction: column-reverse;
    }
`;

export const GraphContainer = styled.div`
    flex-grow: 1;
    min-width: 0;
    position: relative;
    background:
        radial-gradient(ellipse at 50% 40%, rgba(51, 7, 15, 0.35), transparent 70%),
        ${color.void};

    @media (max-width: 860px) {
        height: clamp(420px, 70vh, 640px);
        flex-grow: 0;
    }
`;

// --- Common Components ---
export const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    padding: 0 10px;
    border: 1px solid ${color.line2};
    border-radius: 0;
    background: transparent;
    color: ${color.text2};
    font-family: ${font.body};
    font-size: 12px;
    letter-spacing: 0.12em;
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease;

    &:hover:not(:disabled) {
        border-color: ${color.bloodHi};
        color: ${color.text};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    ${({ $danger }) => $danger && css`
        border-color: rgba(224, 70, 79, 0.5);
        color: ${color.bloodHi};
        &:hover:not(:disabled) { color: ${color.textBright}; background: rgba(200, 50, 62, 0.14); }
    `}

    ${({ $primary }) => $primary && css`
        border-color: ${color.blood};
        background: ${color.blood};
        color: ${color.onBlood};
        &:hover:not(:disabled) { border-color: ${color.bloodHi}; background: ${color.bloodHi}; color: ${color.onBlood}; }
    `}
`;

export const EventTitle = styled.h3`
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-grow: 1;
    font-family: ${font.display};
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.06em;
    color: ${color.text};

    ${({ $isCompleted }) => $isCompleted && css`
        text-decoration: line-through;
        color: ${color.text3};
    `}
`;

export const DueDate = styled.span`
    font-family: ${font.mono};
    font-size: 12px;
    color: ${color.text2};
`;

export const Description = styled.p`
    margin-top: 0;
    font-size: 13px;
    line-height: 1.7;
    color: ${color.text2};
`;

export const SubTaskList = styled.div`
    margin-top: 14px;
    padding-top: 8px;
    border-top: 1px solid ${color.line};
`;

export const SubListTitle = styled.div`
    margin-bottom: 6px;
    font-size: 12px;
    letter-spacing: 0.2em;
    color: ${color.text3};
    b { font-family: ${font.mono}; font-weight: 400; margin-left: 6px; letter-spacing: 0; }
`;

// Checkbox used in nodes and sidebar
export const Check = styled.input.attrs({ type: 'checkbox' })`
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    margin: 0;
    accent-color: ${color.blood};
    cursor: pointer;
    &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

// --- Form & Modal Styles ---
const inputStyles = css`
    width: 100%;
    box-sizing: border-box;
    padding: 0 14px;
    background: rgba(7, 2, 5, 0.7);
    border: 1px solid ${color.line2};
    border-radius: 0;
    color: ${color.text};
    font-family: ${font.body};
    font-size: 15px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder { color: ${color.text3}; }

    &:focus {
        outline: none;
        border-color: ${color.bloodHi};
        box-shadow: 0 0 0 3px rgba(224, 70, 79, 0.18);
    }
`;

export const EditInput = styled.input`
    ${inputStyles}
    height: 44px;
    &[type='datetime-local'] { font-family: ${font.mono}; font-size: 14px; color-scheme: dark; }
`;

export const EditTextarea = styled.textarea`
    ${inputStyles}
    padding: 12px 14px;
    line-height: 1.7;
    resize: vertical;
    min-height: 96px;
`;

export const MoveSelect = styled.select`
    ${inputStyles}
    height: 44px;
    background-color: ${color.void};
    color-scheme: dark;
`;
